import React, { FC, useMemo, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput'
import { Box, Grid } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { ControlledCarrier } from 'apps/fleet-planner/components/create-load/ControlledCarrier'
import { ControlledDispatcher } from 'apps/fleet-planner/components/create-load/ControlledDispatcher'
import { ControlledTrailer } from 'apps/fleet-planner/components/create-load/ControlledTrailer'
import { ControlledTruck } from 'apps/fleet-planner/components/create-load/ControlledTruck'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { useMembershipValidation } from 'apps/fleet-planner/hooks/useMembershipValidation'
import { Driver } from 'apps/fleet-planner/pages/loads/Driver'
import {
  ILoadAssignmentSchemaContext,
  LoadAssignmentSchema,
  LoadAssignmentSchemaType,
} from 'apps/fleet-planner/schema/LoadAssignmentSchema'
import { ICarrier, ILoad, ITrailer, ITruck } from 'apps/fleet-planner/types'
import { formatCarrier } from 'apps/fleet-planner/utils/api/carriers'
import { loadDetailsSelector } from 'apps/fleet-planner/utils/selectors'
import { selectEquipmentState } from 'apps/fleet-planner/utils/selectors/equipment'
import { Button, INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui'
import { QRCodeVerificationModal } from '../modals/QRCodeVerificationModal'
import { EnableLiveTrackingButton } from './EnableLiveTrackingButton'

interface ILoadAssignmentFormProps {
  isLoading?: boolean
  defaultValues?: Partial<LoadAssignmentSchemaType>
  error?: string
  shipmentFlags?: string[]
  /**
   * When present, allows tracking status check for <TruckIcon />
   */
  shipmentId?: string
  onSave: (data: LoadAssignmentSchemaType) => void
  onCancel: () => void
}

/**
 * Ensures that schema-unfriendly values are filtered out
 */
const getDefaultValues = (
  defaultValues: Partial<LoadAssignmentSchemaType>
): Partial<LoadAssignmentSchemaType> => {
  const defaultDriversWithId = defaultValues?.drivers?.filter(d => !!d.id) || []
  return LoadAssignmentSchema.cast({
    ...defaultValues,
    drivers: [...defaultDriversWithId],
  })
}

/**
 * Form for Load Details Modal to edit multiple parts of load at one time
 */

const FRAUD_VALIDATION_FLAG = 'fraud_validation'

export const LoadAssignmentForm: FC<ILoadAssignmentFormProps> = ({
  defaultValues: defaultValuesProp = {},
  onSave,
  onCancel,
  isLoading,
  error,
  shipmentFlags,
  shipmentId,
}) => {
  const { load } = useSelector(loadDetailsSelector)
  const { trucks = [] } = useSelector(selectEquipmentState)
  const { isExternal, status } = load || {}
  const { isMobile } = useBreakpointContext()
  const { getFlag } = useFeatureFlagContext()
  const { enabled: isFpCarrierPortalEnabled } = getFlag('fpCarrierPortal')
  const { enabled: isTrackingButtonEnabled } = getFlag('fmsEnableTrackingButton')
  const { enabled: isFMSELDTrackingEnabled } = getFlag('fmsELDTracking')
  const { enabled: isFpQrConfirmationModalEnabled } = getFlag('fpQrConfirmationModal')
  const [showQRCodeVerificationModalOpen, setShowQRCodeVerificationModal] = useState<boolean>(false)

  const { isAdminForCarrier, isAssociatedCarrier, isRoleTypeForCarrier } = useMembershipValidation()

  const defaultValues = { ...getDefaultValues(defaultValuesProp) }
  const { control, errors, handleSubmit, setValue } = useForm<
    LoadAssignmentSchemaType,
    ILoadAssignmentSchemaContext
  >({
    mode: 'all',
    resolver: yupResolver(LoadAssignmentSchema),
    context: {
      isFpCarrierPortalEnabled,
      isRoleTypeForCarrier,
      isAssociatedCarrier,
      isAdminForCarrier,
      isDriverRequired: !!isExternal && ![LOAD_STATE.Pending].includes(status),
    },
    defaultValues,
  })

  const selectedMotorCarrierId = useWatch<LoadAssignmentSchemaType['motorCarrierId']>({
    name: 'motorCarrierId',
    control,
  })

  const selectedMotorCarrier = useMemo(
    () =>
      formatCarrier({
        id: selectedMotorCarrierId!,
        name: '',
      }),
    [selectedMotorCarrierId]
  )

  const isAdmin = !!(selectedMotorCarrierId && isAdminForCarrier(selectedMotorCarrierId))

  const truckId = useWatch({
    defaultValue: undefined,
    control,
    name: 'truckId',
  })

  const selectedTruck = trucks.find(t => String(t.id) === String(truckId))

  const closeModal = () => setShowQRCodeVerificationModal(false)

  const cancelQRCodeVerificationModalHandler = () => {
    closeModal()
  }

  const confirmQRCodeVerificationModalHandler = () => {
    handleSubmit(onSave)()
    closeModal()
  }

  const submitHandler = (data: LoadAssignmentSchemaType) => {
    const { drivers: previousDrivers = [] } = defaultValues || {}
    const { drivers = [] } = data || {}
    const previousDriversIds = previousDrivers.map(driver => driver.id).sort()
    const driversIds = drivers.map(driver => driver.id).sort()
    const hasBeenDispatched = ![LOAD_STATE.Pending, LOAD_STATE['Ready for Dispatch']].includes(
      status
    )
    const hasUpdatedDrivers =
      driversIds.length === previousDriversIds.length && !isEqual(driversIds, previousDriversIds)

    if (shipmentFlags?.includes(FRAUD_VALIDATION_FLAG) && hasUpdatedDrivers && hasBeenDispatched) {
      setShowQRCodeVerificationModal(true)
      return
    }
    onSave(data)
  }

  const featureFlaggedSubmitHandler = isFpQrConfirmationModalEnabled ? submitHandler : onSave

  return (
    <form onSubmit={handleSubmit(featureFlaggedSubmitHandler)} data-test='load-assignment-form'>
      {/* Carrier */}
      <Box mb={2}>
        <ControlledCarrier
          defaultValue={defaultValues.motorCarrier as ICarrier}
          control={control}
          setValue={setValue}
          errors={errors}
          CarrierProps={{
            /**
             * Only disabled updating carrier for external loads
             */
            disabled: isExternal && !!defaultValues?.motorCarrier?.id,
            validationMessage:
              isExternal && !!defaultValues?.motorCarrier?.id
                ? 'Please contact us if you need to choose a different carrier.'
                : undefined,
          }}
        />
      </Box>
      {/* Driver */}
      <Box>
        <Controller
          control={control}
          name='drivers'
          defaultValue={defaultValues.drivers || []}
          render={({ onChange, value }) => {
            const changeHandler = (change: Partial<ILoad>) => {
              const { drivers: newDrivers = [] } = change || {}
              onChange(newDrivers)
            }

            return (
              <Driver
                value={{ drivers: value }}
                onChange={changeHandler}
                layout='vertical'
                refetchOptions
                large={isMobile}
                errors={get(errors, 'drivers.message', '')}
                isAddEntityEnabled={isFpCarrierPortalEnabled ? isAdmin : true}
                /**
                 * Display Drivers in two groups depending on if
                 * they belong to current carrier
                 */
                selectedMotorCarrier={selectedMotorCarrier}
                isExternal={isExternal}
              />
            )
          }}
        />
      </Box>
      {/* Truck */}
      <Box mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={isExternal ? 6 : 12}>
            <ControlledTruck
              control={control}
              setValue={setValue}
              defaultValue={{
                truck: {
                  id: defaultValues.truckId,
                } as ITruck,
              }}
              TruckProps={{
                error: errors.truckId?.message,
                large: isMobile,
                refetchOptions: true,
                selectedMotorCarrierId,
              }}
            />
          </Grid>
          {isExternal && (
            <Grid item xs={12} md={6}>
              <DeprecatedControlledTextInput
                control={control}
                name='tractorVin'
                TextInputProps={{
                  label: 'Tractor VIN (last 4 digits)',
                  validationMessage: errors.tractorVin?.message,
                  invalid: !!errors.tractorVin?.message,
                  placeholder: '1234',
                }}
                defaultValue={defaultValues.tractorVin}
              />
            </Grid>
          )}
          {/**
           * For external loads, show "Click to enable live tracking"
           * button which checks whether the truck is tracked in project44
           */}
          {isExternal &&
            selectedTruck &&
            shipmentId &&
            isTrackingButtonEnabled &&
            !isFMSELDTrackingEnabled && (
              <Grid item xs={12}>
                <EnableLiveTrackingButton
                  truckNumber={selectedTruck?.truckNumber}
                  shipmentId={shipmentId}
                />
              </Grid>
            )}
        </Grid>
      </Box>
      {/* Trailer */}
      <Box mb={2}>
        <ControlledTrailer
          control={control}
          setValue={setValue}
          defaultValue={{
            trailer: {
              id: defaultValues.trailerId,
              trailerNumber: '',
            } as ITrailer,
          }}
          TrailerProps={{
            error: errors.trailerId?.message,
            large: isMobile,
            refetchOptions: true,
            selectedMotorCarrierId,
          }}
        />
      </Box>
      {/* Dispatcher */}
      <Box mb={1}>
        <ControlledDispatcher
          control={control}
          setValue={setValue}
          DispatcherProps={{
            large: false,
            error: errors.dispatcherId?.message,
            selectedMotorCarrierId,
          }}
          defaultValue={{
            dispatcher: {
              id: defaultValues.dispatcherId as string,
            },
          }}
        />
      </Box>

      {error && (
        <Box mt={2}>
          <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={error} />
        </Box>
      )}

      <div className='tr'>
        <div className='mt4'>
          <Button
            color='tertiary'
            size='large'
            onClick={onCancel}
            data-test='inline-edit-cancel-assignment'>
            Cancel
          </Button>
          <Button
            color='primary'
            size='large'
            type='submit'
            disabled={!!Object.keys({ ...errors }).length || isLoading}
            data-test='inline-edit-save-assignment'>
            {isLoading ? 'Loading...' : 'Save'}
          </Button>
        </div>
      </div>
      <QRCodeVerificationModal
        onConfirm={confirmQRCodeVerificationModalHandler}
        onCancel={cancelQRCodeVerificationModalHandler}
        isOpen={showQRCodeVerificationModalOpen}
      />
    </form>
  )
}
